import React, { useEffect, useMemo, useState, useRef, Children, memo, Component } from 'react';
import { useOrganization } from '../contexts/OrganizationContext';
import { useDebounce } from '../hooks/useDebounce';
import { SearchIcon, DownloadIcon, UsersIcon, TrendingUpIcon, AlertTriangleIcon, LayersIcon, CalendarIcon, BarChart3Icon } from 'lucide-react';
import { ResultPayload } from '../schemas/types';
import { Pagination } from '../components/Pagination';
import { SearchInput } from '../components/SearchInput';
import { MOCK_COHORTS, MOCK_SUB_ORGS, MOCK_RESULTS } from '../schemas/mockData';
// Mock assignments data
const MOCK_ASSIGNMENTS = [{
  id: 'assign-1',
  exerciseId: 'ex-ukrainian-kids',
  exerciseName: 'Ukrainian Children Assessment',
  cohortId: 'cohort-ukr-0-0',
  orgId: 'org-kmg'
}, {
  id: 'assign-2',
  exerciseId: 'ex-ukrainian-kids',
  exerciseName: 'Ukrainian Children Assessment',
  cohortId: 'cohort-ukr-1-0',
  orgId: 'org-kmg'
}, {
  id: 'assign-3',
  exerciseId: 'ex-mvcc-wellness',
  exerciseName: 'Student Wellness Check-in',
  cohortId: 'cohort-mvcc-1',
  orgId: 'org-mvcc'
}, {
  id: 'assign-4',
  exerciseId: 'ex-duquesne-wellness',
  exerciseName: 'Campus Mental Health Survey',
  cohortId: 'cohort-duq-0-0',
  orgId: 'org-duquesne'
}];
export const Results = memo(() => {
  const {
    language,
    currentOrganization
  } = useOrganization();
  const [view, setView] = useState<'individual' | 'cohort' | 'assignment' | 'trends'>('individual');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSubOrg, setSelectedSubOrg] = useState<string>('all');
  const [selectedCohort, setSelectedCohort] = useState<string>('all');
  const [selectedAssignment, setSelectedAssignment] = useState<string>('all');
  const [showAtRiskOnly, setShowAtRiskOnly] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;
  // Debounce search query for better performance
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  // Complete translations
  const t = {
    results: language === 'en' ? 'Results' : language === 'uk' ? 'Результати' : 'Résultats',
    resultsSubtitle: language === 'en' ? 'View and analyze assessment results across your organization' : language === 'uk' ? 'Переглядайте та аналізуйте результати оцінювання у вашій організації' : 'Voir et analyser les résultats des évaluations dans votre organisation',
    downloadCSV: language === 'en' ? 'Download CSV' : language === 'uk' ? 'Завантажити CSV' : 'Télécharger CSV',
    individualView: language === 'en' ? 'Individual Results' : language === 'uk' ? 'Індивідуальні результати' : 'Résultats individuels',
    cohortAnalysis: language === 'en' ? 'Cohort Analysis' : language === 'uk' ? 'Аналіз когорти' : 'Analyse de cohorte',
    byAssignment: language === 'en' ? 'By Assignment' : language === 'uk' ? 'За призначенням' : 'Par affectation',
    trendsView: language === 'en' ? 'Trends' : language === 'uk' ? 'Тренди' : 'Tendances',
    trendsPlaceholder: language === 'en' ? 'Longitudinal trend analysis will be available here' : language === 'uk' ? 'Поздовжній аналіз трендів буде доступний тут' : "L'analyse des tendances longitudinales sera disponible ici",
    totalResults: language === 'en' ? 'Total Results' : language === 'uk' ? 'Всього результатів' : 'Résultats totaux',
    activeCohorts: language === 'en' ? 'Active Cohorts' : language === 'uk' ? 'Активні когорти' : 'Cohortes actives',
    atRiskFlagged: language === 'en' ? 'At-Risk Flagged' : language === 'uk' ? 'Позначено як ризикові' : 'Signalé à risque',
    avgPositiveAffect: language === 'en' ? 'Avg Positive Affect' : language === 'uk' ? 'Середній позитивний афект' : 'Affect positif moyen',
    searchByUserId: language === 'en' ? 'Search by user ID...' : language === 'uk' ? 'Пошук за ID користувача...' : "Rechercher par ID d'utilisateur...",
    allSubOrganizations: language === 'en' ? 'All Sub-Organizations' : language === 'uk' ? 'Всі під-організації' : 'Toutes les sous-organisations',
    allCohorts: language === 'en' ? 'All Cohorts' : language === 'uk' ? 'Всі когорти' : 'Toutes les cohortes',
    atRiskOnly: language === 'en' ? 'At-Risk Only' : language === 'uk' ? 'Тільки ризикові' : 'À risque seulement',
    userId: language === 'en' ? 'User ID' : language === 'uk' ? 'ID користувача' : "ID d'utilisateur",
    cohort: language === 'en' ? 'Cohort' : language === 'uk' ? 'Когорта' : 'Cohorte',
    subOrg: language === 'en' ? 'Sub-Org' : language === 'uk' ? 'Під-організація' : 'Sous-org',
    positive: language === 'en' ? 'Positive' : language === 'uk' ? 'Позитивний' : 'Positif',
    negative: language === 'en' ? 'Negative' : language === 'uk' ? 'Негативний' : 'Négatif',
    risk: language === 'en' ? 'Risk' : language === 'uk' ? 'Ризик' : 'Risque',
    completed: language === 'en' ? 'Completed' : language === 'uk' ? 'Завершено' : 'Terminé',
    atRisk: language === 'en' ? 'At-Risk' : language === 'uk' ? 'Ризиковий' : 'À risque',
    normal: language === 'en' ? 'Normal' : language === 'uk' ? 'Нормальний' : 'Normal',
    noResultsFound: language === 'en' ? 'No results found' : language === 'uk' ? 'Результатів не знайдено' : 'Aucun résultat trouvé',
    cohortComparison: language === 'en' ? 'Cohort Comparison' : language === 'uk' ? 'Порівняння когорт' : 'Comparaison de cohortes',
    cohortComparisonDesc: language === 'en' ? 'Compare emotional state metrics across cohorts' : language === 'uk' ? 'Порівняйте показники емоційного стану між когортами' : "Comparer les métriques d'état émotionnel entre les cohortes",
    resultsByAssignment: language === 'en' ? 'Results by Assignment' : language === 'uk' ? 'Результати за призначенням' : 'Résultats par affectation',
    resultsByAssignmentDesc: language === 'en' ? 'View results grouped by exercise assignments' : language === 'uk' ? 'Переглянути результати, згруповані за призначеннями вправ' : "Voir les résultats groupés par affectations d'exercices",
    allAssignments: language === 'en' ? 'All Assignments' : language === 'uk' ? 'Всі призначення' : 'Toutes les affectations',
    trendsAnalysis: language === 'en' ? 'Trends Analysis' : language === 'uk' ? 'Аналіз трендів' : 'Analyse des tendances',
    results_plural: language === 'en' ? 'results' : language === 'uk' ? 'результатів' : 'résultats',
    completion: language === 'en' ? 'Completion' : language === 'uk' ? 'Завершення' : 'Achèvement',
    avgPositive: language === 'en' ? 'Avg Positive' : language === 'uk' ? 'Середній позитивний' : 'Positif moyen',
    avgPositiveAffectLabel: language === 'en' ? 'Avg Positive Affect' : language === 'uk' ? 'Середній позитивний афект' : 'Affect positif moyen',
    avgNegativeAffectLabel: language === 'en' ? 'Avg Negative Affect' : language === 'uk' ? 'Середній негативний афект' : 'Affect négatif moyen'
  };
  // Memoize organization data
  const orgResults = useMemo(() => MOCK_RESULTS.filter(r => r.deliveredToSpaces.orgId.includes(currentOrganization.spacesOrgId)), [currentOrganization.spacesOrgId]);
  const orgCohorts = useMemo(() => MOCK_COHORTS.filter(c => c.orgId === currentOrganization.id), [currentOrganization.id]);
  const orgSubOrgs = useMemo(() => MOCK_SUB_ORGS.filter(s => s.orgId === currentOrganization.id), [currentOrganization.id]);
  const orgAssignments = useMemo(() => MOCK_ASSIGNMENTS.filter(a => a.orgId === currentOrganization.id), [currentOrganization.id]);
  // Memoize filtered results with debounced search
  const filteredResults = useMemo(() => {
    return orgResults.filter(result => {
      if (showAtRiskOnly && !result.atRisk) return false;
      if (selectedSubOrg !== 'all') {
        const subOrg = orgSubOrgs.find(s => s.id === selectedSubOrg);
        if (subOrg && !result.school?.includes(subOrg.name)) return false;
      }
      if (selectedCohort !== 'all') {
        const cohort = orgCohorts.find(c => c.id === selectedCohort);
        if (cohort && result.class !== cohort.name) return false;
      }
      if (debouncedSearchQuery) {
        const query = debouncedSearchQuery.toLowerCase();
        if (!result.externalUserId.toLowerCase().includes(query)) {
          return false;
        }
      }
      return true;
    });
  }, [orgResults, showAtRiskOnly, selectedSubOrg, selectedCohort, debouncedSearchQuery, orgSubOrgs, orgCohorts]);
  // Pagination
  const totalPages = Math.ceil(filteredResults.length / itemsPerPage);
  const paginatedResults = filteredResults.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  // Calculate cohort statistics
  const cohortStats = orgCohorts.slice(0, 20).map(cohort => {
    const cohortResults = orgResults.filter(r => r.class === cohort.name);
    const atRiskCount = cohortResults.filter(r => r.atRisk).length;
    const avgPositive = cohortResults.length > 0 ? cohortResults.reduce((sum, r) => sum + r.computed.positiveAffect, 0) / cohortResults.length : 0;
    const avgNegative = cohortResults.length > 0 ? cohortResults.reduce((sum, r) => sum + r.computed.negativeAffect, 0) / cohortResults.length : 0;
    return {
      cohort,
      totalResults: cohortResults.length,
      atRiskCount,
      atRiskPercentage: cohortResults.length > 0 ? Math.round(atRiskCount / cohortResults.length * 100) : 0,
      avgPositive: Math.round(avgPositive),
      avgNegative: Math.round(avgNegative)
    };
  });
  return <div className="h-full flex flex-col bg-gray-50" data-id="element-2278">
      {/* Sticky Header */}
      <div className="sticky top-0 z-30 bg-white border-b border-gray-200 shadow-sm" data-id="element-2279">
        <div className="max-w-[1600px] mx-auto px-8 py-6" data-id="element-2280">
          <div className="flex items-center justify-between" data-id="element-2281">
            <div data-id="element-2282">
              <h1 className="text-2xl font-semibold text-gray-900" data-id="element-2283">
                {t.results}
              </h1>
              <p className="text-sm text-gray-600 mt-1" data-id="element-2284">{t.resultsSubtitle}</p>
            </div>
            <button className="px-4 py-2.5 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all flex items-center gap-2 font-medium text-gray-700 shadow-sm" data-id="element-2285">
              <DownloadIcon className="w-4 h-4" data-id="element-2286" />
              {t.downloadCSV}
            </button>
          </div>
        </div>
      </div>
      {/* Stats Cards */}
      <div className="bg-gray-50 border-b border-gray-200" data-id="element-2287">
        <div className="max-w-[1600px] mx-auto px-8 py-6" data-id="element-2288">
          <div className="stats-grid" data-id="element-2289">
            <div className="stat-card" data-id="element-2290">
              <div className="stat-card-icon bg-blue-100" data-id="element-2291">
                <BarChart3Icon className="w-5 h-5 text-blue-600" data-id="element-2292" />
              </div>
              <div className="stat-card-value" data-id="element-2293">
                {orgResults.length.toLocaleString()}
              </div>
              <div className="stat-card-label" data-id="element-2294">{t.totalResults}</div>
            </div>
            <div className="stat-card" data-id="element-2295">
              <div className="stat-card-icon bg-green-100" data-id="element-2296">
                <UsersIcon className="w-5 h-5 text-green-600" data-id="element-2297" />
              </div>
              <div className="stat-card-value" data-id="element-2298">{orgCohorts.length}</div>
              <div className="stat-card-label" data-id="element-2299">{t.activeCohorts}</div>
            </div>
            <div className="stat-card" data-id="element-2300">
              <div className="stat-card-icon bg-red-100" data-id="element-2301">
                <AlertTriangleIcon className="w-5 h-5 text-red-600" data-id="element-2302" />
              </div>
              <div className="stat-card-value" data-id="element-2303">
                {orgResults.filter(r => r.atRisk).length.toLocaleString()}
              </div>
              <div className="stat-card-label" data-id="element-2304">{t.atRiskFlagged}</div>
            </div>
            <div className="stat-card" data-id="element-2305">
              <div className="stat-card-icon bg-purple-100" data-id="element-2306">
                <TrendingUpIcon className="w-5 h-5 text-purple-600" data-id="element-2307" />
              </div>
              <div className="stat-card-value" data-id="element-2308">
                {orgResults.length > 0 ? Math.round(orgResults.reduce((sum, r) => sum + r.computed.positiveAffect, 0) / orgResults.length * 100) / 100 : 0}
              </div>
              <div className="stat-card-label" data-id="element-2309">{t.avgPositiveAffect}</div>
            </div>
          </div>
        </div>
      </div>
      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto" data-id="element-2310">
        <div className="max-w-[1600px] mx-auto px-8 py-6" data-id="element-2311">
          <div className="content-card" data-id="element-2312">
            {/* Sticky Tabs & Filters */}
            <div className="sticky top-0 z-20 bg-white border-b border-gray-200 rounded-t-lg" data-id="element-2313">
              {/* Tabs */}
              <div className="px-6 pt-5 pb-4" data-id="element-2314">
                <div className="flex items-center gap-2" data-id="element-2315">
                  <button onClick={() => {
                  setView('individual');
                  setCurrentPage(1);
                }} className={`px-4 py-2.5 rounded-lg font-medium transition-all text-sm flex items-center gap-2 ${view === 'individual' ? 'bg-blue-50 text-blue-700 border-2 border-blue-200' : 'text-gray-600 hover:bg-gray-50 border-2 border-transparent'}`} data-id="element-2316">
                    <BarChart3Icon className="w-4 h-4" data-id="element-2317" />
                    {t.individualView}
                  </button>
                  <button onClick={() => setView('cohort')} className={`px-4 py-2.5 rounded-lg font-medium transition-all text-sm flex items-center gap-2 ${view === 'cohort' ? 'bg-blue-50 text-blue-700 border-2 border-blue-200' : 'text-gray-600 hover:bg-gray-50 border-2 border-transparent'}`} data-id="element-2318">
                    <LayersIcon className="w-4 h-4" data-id="element-2319" />
                    {t.cohortAnalysis}
                  </button>
                  <button onClick={() => setView('assignment')} className={`px-4 py-2.5 rounded-lg font-medium transition-all text-sm flex items-center gap-2 ${view === 'assignment' ? 'bg-blue-50 text-blue-700 border-2 border-blue-200' : 'text-gray-600 hover:bg-gray-50 border-2 border-transparent'}`} data-id="element-2320">
                    <CalendarIcon className="w-4 h-4" data-id="element-2321" />
                    {t.byAssignment}
                  </button>
                  <button onClick={() => setView('trends')} className={`px-4 py-2.5 rounded-lg font-medium transition-all text-sm flex items-center gap-2 ${view === 'trends' ? 'bg-blue-50 text-blue-700 border-2 border-blue-200' : 'text-gray-600 hover:bg-gray-50 border-2 border-transparent'}`} data-id="element-2322">
                    <TrendingUpIcon className="w-4 h-4" data-id="element-2323" />
                    {t.trendsView}
                  </button>
                </div>
              </div>
              {/* Filters - Only for Individual View - INCREASED PADDING */}
              {view === 'individual' && <div className="px-6 py-4 bg-gray-50 border-t border-gray-200" data-id="element-2324">
                  <div className="flex items-center gap-3" data-id="element-2325">
                    {/* Search Input Component */}
                    <SearchInput value={searchQuery} onChange={setSearchQuery} placeholder={t.searchByUserId} className="flex-1 max-w-xs" data-id="element-2326" />
                    <select value={selectedSubOrg} onChange={e => setSelectedSubOrg(e.target.value)} className="h-10 px-4 text-sm border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-w-[180px] transition-all bg-white" data-id="element-2327">
                      <option value="all" data-id="element-2328">{t.allSubOrganizations}</option>
                      {orgSubOrgs.slice(0, 20).map(org => <option key={org.id} value={org.id} data-id="element-2329">
                          {org.name}
                        </option>)}
                    </select>
                    <select value={selectedCohort} onChange={e => setSelectedCohort(e.target.value)} className="h-10 px-4 text-sm border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-w-[180px] transition-all bg-white" data-id="element-2330">
                      <option value="all" data-id="element-2331">{t.allCohorts}</option>
                      {orgCohorts.slice(0, 20).map(cohort => <option key={cohort.id} value={cohort.id} data-id="element-2332">
                          {cohort.name} - {cohort.schoolName}
                        </option>)}
                    </select>
                    <button onClick={() => setShowAtRiskOnly(!showAtRiskOnly)} className={`h-10 px-4 text-sm rounded-lg font-medium transition-all flex items-center gap-2 whitespace-nowrap ${showAtRiskOnly ? 'bg-red-50 text-red-700 border-2 border-red-200' : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-gray-300'}`} data-id="element-2333">
                      <AlertTriangleIcon className="w-4 h-4" data-id="element-2334" />
                      {t.atRiskOnly}
                    </button>
                  </div>
                </div>}
            </div>
            {/* Individual View */}
            {view === 'individual' && <div className="p-6" data-id="element-2335">
                <div className="table-container" data-id="element-2336">
                  <div className="table-scrollable" data-id="element-2337">
                    <table className="enterprise-table" data-id="element-2338">
                      <thead data-id="element-2339">
                        <tr data-id="element-2340">
                          <th className="cell-left" data-id="element-2341">{t.userId}</th>
                          <th className="cell-left" data-id="element-2342">{t.cohort}</th>
                          <th className="cell-left" data-id="element-2343">{t.subOrg}</th>
                          <th className="cell-left" data-id="element-2344">{t.positive}</th>
                          <th className="cell-left" data-id="element-2345">{t.negative}</th>
                          <th className="cell-left" data-id="element-2346">{t.risk}</th>
                          <th className="cell-left" data-id="element-2347">{t.completed}</th>
                        </tr>
                      </thead>
                      <tbody data-id="element-2348">
                        {paginatedResults.map((result, idx) => <tr key={idx} className="hover:bg-gray-50 transition-colors" data-id="element-2349">
                            <td data-id="element-2350">
                              <span className="table-id" data-id="element-2351">
                                {result.externalUserId}
                              </span>
                            </td>
                            <td data-id="element-2352">
                              <span className="text-sm text-gray-700" data-id="element-2353">
                                {result.class}
                              </span>
                            </td>
                            <td data-id="element-2354">
                              <span className="text-sm text-gray-700" data-id="element-2355">
                                {result.school}
                              </span>
                            </td>
                            <td data-id="element-2356">
                              <div className="flex items-center gap-2" data-id="element-2357">
                                <div className="w-16 bg-gray-200 rounded-full h-1.5" data-id="element-2358">
                                  <div className="bg-green-500 h-1.5 rounded-full transition-all" style={{
                              width: `${result.computed.positiveAffect}%`
                            }} data-id="element-2359" />
                                </div>
                                <span className="text-xs font-semibold text-gray-900 w-8 text-right" data-id="element-2360">
                                  {result.computed.positiveAffect}
                                </span>
                              </div>
                            </td>
                            <td data-id="element-2361">
                              <div className="flex items-center gap-2" data-id="element-2362">
                                <div className="w-16 bg-gray-200 rounded-full h-1.5" data-id="element-2363">
                                  <div className="bg-red-500 h-1.5 rounded-full transition-all" style={{
                              width: `${result.computed.negativeAffect}%`
                            }} data-id="element-2364" />
                                </div>
                                <span className="text-xs font-semibold text-gray-900 w-8 text-right" data-id="element-2365">
                                  {result.computed.negativeAffect}
                                </span>
                              </div>
                            </td>
                            <td data-id="element-2366">
                              {result.atRisk ? <span className="table-badge at-risk" data-id="element-2367">
                                  {t.atRisk}
                                </span> : <span className="table-badge status-active" data-id="element-2368">
                                  {t.normal}
                                </span>}
                            </td>
                            <td data-id="element-2369">
                              <span className="text-xs text-gray-600" data-id="element-2370">
                                {new Date(result.completedAt).toLocaleDateString()}
                              </span>
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>
                {filteredResults.length > itemsPerPage && <div className="mt-6" data-id="element-2371">
                    <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={filteredResults.length} itemsPerPage={itemsPerPage} onPageChange={setCurrentPage} data-id="element-2372" />
                  </div>}
                {filteredResults.length === 0 && <div className="table-empty-state" data-id="element-2373">
                    <BarChart3Icon className="table-empty-icon" data-id="element-2374" />
                    <p className="table-empty-title" data-id="element-2375">{t.noResultsFound}</p>
                  </div>}
              </div>}
            {/* Cohort Analysis View */}
            {view === 'cohort' && <div className="p-6" data-id="element-2376">
                <div className="mb-6" data-id="element-2377">
                  <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-2378">
                    {t.cohortComparison}
                  </h3>
                  <p className="text-sm text-gray-600" data-id="element-2379">
                    {t.cohortComparisonDesc}
                  </p>
                </div>
                <div className="space-y-4" data-id="element-2380">
                  {cohortStats.map(stat => <div key={stat.cohort.id} className="bg-white border-2 border-gray-200 rounded-lg p-5 hover:shadow-md hover:border-blue-300 transition-all" data-id="element-2381">
                      <div className="flex items-center justify-between mb-4" data-id="element-2382">
                        <div className="flex items-center gap-3" data-id="element-2383">
                          <div className="w-11 h-11 bg-purple-100 rounded-lg flex items-center justify-center" data-id="element-2384">
                            <LayersIcon className="w-6 h-6 text-purple-600" data-id="element-2385" />
                          </div>
                          <div data-id="element-2386">
                            <h4 className="text-base font-bold text-gray-900" data-id="element-2387">
                              {stat.cohort.name}
                            </h4>
                            <p className="text-sm text-gray-600" data-id="element-2388">
                              {stat.cohort.schoolName} • {stat.totalResults}{' '}
                              {t.results_plural}
                            </p>
                          </div>
                        </div>
                        {stat.atRiskCount > 0 && <div className="px-4 py-2 bg-red-50 border-2 border-red-200 rounded-lg" data-id="element-2389">
                            <div className="text-2xl font-bold text-red-700" data-id="element-2390">
                              {stat.atRiskPercentage}%
                            </div>
                            <div className="text-xs text-red-600 font-semibold uppercase tracking-wide" data-id="element-2391">
                              {t.atRisk}
                            </div>
                          </div>}
                      </div>
                      <div className="grid grid-cols-2 gap-4" data-id="element-2392">
                        <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4" data-id="element-2393">
                          <div className="text-xs text-green-600 font-semibold mb-1 uppercase tracking-wide" data-id="element-2394">
                            {t.avgPositiveAffectLabel}
                          </div>
                          <div className="text-3xl font-bold text-green-700" data-id="element-2395">
                            {stat.avgPositive}
                          </div>
                        </div>
                        <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4" data-id="element-2396">
                          <div className="text-xs text-red-600 font-semibold mb-1 uppercase tracking-wide" data-id="element-2397">
                            {t.avgNegativeAffectLabel}
                          </div>
                          <div className="text-3xl font-bold text-red-700" data-id="element-2398">
                            {stat.avgNegative}
                          </div>
                        </div>
                      </div>
                    </div>)}
                </div>
              </div>}
            {/* Assignment View */}
            {view === 'assignment' && <div className="p-6" data-id="element-2399">
                <div className="mb-6" data-id="element-2400">
                  <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-2401">
                    {t.resultsByAssignment}
                  </h3>
                  <p className="text-sm text-gray-600" data-id="element-2402">
                    {t.resultsByAssignmentDesc}
                  </p>
                </div>
                <div className="mb-4" data-id="element-2403">
                  <select value={selectedAssignment} onChange={e => setSelectedAssignment(e.target.value)} className="h-10 px-3 text-sm border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-2404">
                    <option value="all" data-id="element-2405">{t.allAssignments}</option>
                    {orgAssignments.map(assignment => <option key={assignment.id} value={assignment.id} data-id="element-2406">
                        {assignment.exerciseName} -{' '}
                        {orgCohorts.find(c => c.id === assignment.cohortId)?.name}
                      </option>)}
                  </select>
                </div>
                <div className="space-y-3" data-id="element-2407">
                  {orgAssignments.map(assignment => {
                const assignmentResults = orgResults.filter(r => r.exerciseId === assignment.exerciseId);
                const cohort = orgCohorts.find(c => c.id === assignment.cohortId);
                const atRiskCount = assignmentResults.filter(r => r.atRisk).length;
                const totalExpected = cohort?.participantCount || 30;
                return <div key={assignment.id} className="bg-white border-2 border-gray-200 rounded-lg p-5" data-id="element-2408">
                        <div className="flex items-center justify-between mb-4" data-id="element-2409">
                          <div data-id="element-2410">
                            <h4 className="text-lg font-bold text-gray-900" data-id="element-2411">
                              {assignment.exerciseName}
                            </h4>
                            <p className="text-sm text-gray-600" data-id="element-2412">
                              {t.cohort}: {cohort?.name} • {cohort?.schoolName}
                            </p>
                          </div>
                          <div className="text-right" data-id="element-2413">
                            <div className="text-2xl font-bold text-gray-900" data-id="element-2414">
                              {assignmentResults.length}
                            </div>
                            <div className="text-sm text-gray-600" data-id="element-2415">
                              / {totalExpected} {t.results_plural}
                            </div>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-4" data-id="element-2416">
                          <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-4" data-id="element-2417">
                            <div className="text-sm text-gray-600 mb-1" data-id="element-2418">
                              {t.completion}
                            </div>
                            <div className="text-2xl font-bold text-gray-900" data-id="element-2419">
                              {Math.round(assignmentResults.length / totalExpected * 100)}
                              %
                            </div>
                          </div>
                          <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4" data-id="element-2420">
                            <div className="text-sm text-red-600 mb-1" data-id="element-2421">
                              {t.atRisk}
                            </div>
                            <div className="text-2xl font-bold text-red-700" data-id="element-2422">
                              {atRiskCount}
                            </div>
                          </div>
                          <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4" data-id="element-2423">
                            <div className="text-sm text-green-600 mb-1" data-id="element-2424">
                              {t.avgPositive}
                            </div>
                            <div className="text-2xl font-bold text-green-700" data-id="element-2425">
                              {assignmentResults.length > 0 ? Math.round(assignmentResults.reduce((sum, r) => sum + r.computed.positiveAffect, 0) / assignmentResults.length) : 0}
                            </div>
                          </div>
                        </div>
                      </div>;
              })}
                </div>
              </div>}
            {/* Trends View */}
            {view === 'trends' && <div className="p-6" data-id="element-2426">
                <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-12 text-center" data-id="element-2427">
                  <TrendingUpIcon className="w-20 h-20 text-blue-600 mx-auto mb-6" data-id="element-2428" />
                  <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-2429">
                    {t.trendsAnalysis}
                  </h3>
                  <p className="text-base text-gray-600" data-id="element-2430">
                    {t.trendsPlaceholder}
                  </p>
                </div>
              </div>}
          </div>
        </div>
      </div>
    </div>;
});
Results.displayName = 'Results';